// src/pages/appointments/edit/EditAppointment.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import ContextFeedbackLauncher from "@/components/feedback/ContextFeedbackLauncher.jsx";
import {
  getDoctorBookableDates,
  getDoctorBookingAvailability,
} from "@/services/doctorAvailabilityService.js";
import { resolveDoctorBookingEntryState } from "@/services/doctorBookingMode.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

async function filterDatesWithVisibleSlots({
  doctorId,
  dates,
  requestedDuration,
  excludeAppointmentId = "",
}) {
  const candidateDates = Array.isArray(dates) ? dates : [];
  if (!doctorId || candidateDates.length === 0) return [];

  const checks = await Promise.all(
    candidateDates.map(async (dateISO) => {
      const result = await getDoctorBookingAvailability({
        doctorId,
        dateISO,
        requestedDuration: requestedDuration || null,
        excludeAppointmentId,
      });
      const hasFutureAvailableSlots = (result?.slots || []).some(
        (slot) => slot?.status === "available" && slot?.available === true
      );
      return hasFutureAvailableSlots ? dateISO : "";
    })
  );

  return checks.filter(Boolean);
}

function useAvailabilityClock() {
  const [clock, setClock] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let timeoutId = 0;
    const refreshClock = () => setClock(Date.now());
    const scheduleNextTick = () => {
      const now = Date.now();
      const msUntilNextMinute = 60000 - (now % 60000);
      timeoutId = window.setTimeout(() => {
        refreshClock();
        scheduleNextTick();
      }, msUntilNextMinute || 60000);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshClock();
      }
    };

    scheduleNextTick();
    window.addEventListener("focus", refreshClock);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("focus", refreshClock);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return clock;
}

function safeJSONParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function restoreAppointmentDraft(id, locationState) {
  if (locationState && Object.keys(locationState || {}).length) return locationState;

  try {
    const raw = sessionStorage.getItem(`afrimed:appointment:${String(id)}`);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed) return parsed;
  } catch {}

  try {
    const raw = sessionStorage.getItem("afrimed:appointment:last");
    const parsed = raw ? safeJSONParse(raw) : null;
    const parsedId = String(parsed?.appointmentId || parsed?.id || "").trim();
    if (parsed && parsedId && parsedId === String(id || "").trim()) return parsed;
  } catch {}

  return null;
}

function persistAppointmentSnapshot(id, payload) {
  if (!payload) return;
  try {
    sessionStorage.setItem(`afrimed:appointment:${id}`, JSON.stringify(payload));
    sessionStorage.setItem("afrimed:appointment:last", JSON.stringify(payload));
  } catch {}
}

function persistAppointmentSnapshotEverywhere(id, payload) {
  if (!payload) return;
  try {
    sessionStorage.setItem(`afrimed:appointment:${String(id)}`, JSON.stringify(payload));
    sessionStorage.setItem("afrimed:appointment:last", JSON.stringify(payload));
    sessionStorage.setItem(`afrimed:doctor_appt:${String(id)}`, JSON.stringify(payload));
  } catch {}
}

function formatBookableDateParts(dateISO, locale = "en-US") {
  const parsed = new Date(`${dateISO}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return {
      weekday: "",
      day: "",
      month: "",
    };
  }

  return {
    weekday: parsed.toLocaleDateString(locale, { weekday: "short" }),
    day: parsed.toLocaleDateString(locale, { day: "numeric" }),
    month: parsed.toLocaleDateString(locale, { month: "short" }),
  };
}

function normalizeDurationMinutes(value, fallback = 30) {
  const minutes = Number(value);
  if (Number.isFinite(minutes) && minutes > 0) return Math.round(minutes);
  return fallback;
}

function parseAmount(value) {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  if (typeof value === "string") {
    const match = value.match(/-?\d+(\.\d+)?/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function prettyPaymentMethod(value, fallback = "Debit / Credit cards") {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return fallback;

  const map = {
    credit: "Credit / Debit cards",
    debit: "Credit / Debit cards",
    card: "Credit / Debit cards",
    bank: "Bank transfers",
    transfer: "Bank transfers",
    eft: "Instant EFT",
    cash: "Cash on service",
    wallet: "E-wallets",
  };

  return map[raw] || fallback;
}

export default function EditAppointment() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { t, languageCode, formatDate } = useLanguage();

  const restored = React.useMemo(
    () => restoreAppointmentDraft(id, location.state),
    [id, location.state]
  );

  const doctor = restored?.doctor ?? null;
  const doctorId = String(
    doctor?.id ||
      doctor?.user_id ||
      restored?.doctorId ||
      restored?.doctor_id ||
      restored?.doctor?.id ||
      restored?.doctor?.user_id ||
      ""
  ).trim();
  const actorRole = restored?.actorRole === "doctor" ? "doctor" : "patient";
  const currentMode =
    restored?.previousMode ||
    restored?.previous_mode ||
    restored?.mode ||
    restored?.visitMode ||
    (String(restored?.consultation_type || "").trim().toLowerCase() === "private" ? "evisit" : "inperson");
  const currentConsultationType =
    restored?.previousConsultationType ||
    restored?.previous_consultation_type ||
    restored?.consultation_type ||
    (currentMode === "evisit" ? "private" : "moh");

  // appointment mode
  const [mode, setMode] = React.useState(
    String(restored?.mode || "").trim().toLowerCase() === "evisit" ? "evisit" : "inperson"
  );

  // facility (used when in-person)
  const restoredFacility = restored?.facility || null;
  const facilityId = restored?.facilityId || restoredFacility?.id || restored?.practice_id || null;
  const facilityName = restored?.facilityName || restoredFacility?.name || "";

  // local edit state
  const [selectedDate, setSelectedDate] = React.useState(
    restored?.selectedDate || restored?.when?.dateISO || todayISO()
  );
  const [selectedTime, setSelectedTime] = React.useState(
    restored?.selectedTime || restored?.when?.time || ""
  );
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [slotState, setSlotState] = React.useState({
    loading: false,
    error: "",
    slots: [],
    maxDateISO: null,
    workingDay: null,
  });
  const [bookableDateState, setBookableDateState] = React.useState({
    loading: false,
    error: "",
    dates: [],
    maxDateISO: null,
  });
  const [modeAccessState, setModeAccessState] = React.useState({
    loading: false,
    evisitEnabled: true,
    hasInPersonOption: true,
  });
  const availabilityClock = useAvailabilityClock();

  const paymentMethodLabel =
    restored?.paymentMethodLabel ||
    restored?.paymentLabel ||
    prettyPaymentMethod(restored?.paymentMethod, "") ||
    t("appointments.edit.paymentMethodFallback", "Debit / Credit cards");

  const amountPaid =
    parseAmount(
      restored?.amountPaid ??
        restored?.amount_paid ??
        restored?.booked_service_price ??
        restored?.serviceNamePrice
    );
  const currency = restored?.currency || restored?.doctor?.currency || "BWP";

  const needsFacility = mode === "inperson";
  const hasFacility = Boolean(
    facilityId ||
      facilityName ||
      restoredFacility?.name ||
      restoredFacility?.address
  );

  React.useEffect(() => {
    let alive = true;

    async function hydrateModeAccess() {
      if (!doctorId) return;
      setModeAccessState((prev) => ({ ...prev, loading: true }));

      try {
        const resolved = await resolveDoctorBookingEntryState({
          doctorId,
          doctor,
          requestedMode: mode,
          consultationType: restored?.consultation_type || null,
          facilityId,
          facilityName,
          facility: restoredFacility,
        });
        if (!alive) return;

        setModeAccessState({
          loading: false,
          evisitEnabled: resolved.evisitEnabled,
          hasInPersonOption: resolved.hasInPersonOption,
        });

        if (resolved.mode && resolved.mode !== mode) {
          setMode(resolved.mode);
        }
      } catch {
        if (!alive) return;
        setModeAccessState((prev) => ({ ...prev, loading: false }));
      }
    }

    hydrateModeAccess();
    return () => {
      alive = false;
    };
  }, [doctor, doctorId, facilityId, facilityName, mode, restored?.consultation_type, restoredFacility]);

  const requestedDuration = React.useMemo(
    () =>
      normalizeDurationMinutes(
        restored?.durationMinutes ||
          restored?.duration_minutes ||
          restored?.duration ||
          restored?.serviceDurationMinutes
      ),
    [restored]
  );

  React.useEffect(() => {
    let alive = true;

    async function hydrateBookableDates() {
      if (!doctorId) {
        if (alive) {
          setBookableDateState({
            loading: false,
            error: "",
            dates: [],
            maxDateISO: null,
          });
        }
        return;
      }

      setBookableDateState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const result = await getDoctorBookableDates({ doctorId });
        if (!alive) return;

        const nextDates = await filterDatesWithVisibleSlots({
          doctorId,
          dates: Array.isArray(result?.dates) ? result.dates : [],
          requestedDuration,
          excludeAppointmentId: id,
        });
        if (!alive) return;
        setBookableDateState({
          loading: false,
          error: "",
          dates: nextDates,
          maxDateISO: result?.maxDateISO || null,
        });

        setSelectedDate((current) => {
          if (nextDates.length === 0) return "";
          return current && nextDates.includes(current) ? current : nextDates[0];
        });
      } catch (error) {
        if (!alive) return;
        setBookableDateState({
          loading: false,
          error:
            error?.message ||
            t("appointments.edit.errors.loadDates", "Unable to load available dates."),
          dates: [],
          maxDateISO: null,
        });
      }
    }

    hydrateBookableDates();
    return () => {
      alive = false;
    };
  }, [availabilityClock, doctorId, id, requestedDuration, t]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateAvailability() {
      if (!doctorId || !selectedDate) {
        if (alive) {
          setSlotState({
            loading: false,
            error: "",
            slots: [],
            maxDateISO: null,
            workingDay: null,
          });
        }
        return;
      }

      setSlotState((prev) => ({ ...prev, loading: true, error: "" }));
      try {
        const result = await getDoctorBookingAvailability({
          doctorId,
          dateISO: selectedDate,
          requestedDuration,
          excludeAppointmentId: id,
        });
        if (!alive) return;
        setSlotState({
          loading: false,
          error: "",
          slots: result?.slots || [],
          maxDateISO: result?.maxDateISO || null,
          workingDay: result?.workingDay || null,
        });
      } catch (error) {
        if (!alive) return;
        setSlotState({
          loading: false,
          error:
            error?.message ||
            t("appointments.edit.errors.loadTimes", "Unable to load available times."),
          slots: [],
          maxDateISO: null,
          workingDay: null,
        });
      }
    }

    hydrateAvailability();
    return () => {
      alive = false;
    };
  }, [availabilityClock, doctorId, requestedDuration, selectedDate, t]);

  const visibleSlotEntries = React.useMemo(
    () => (slotState.slots || []).filter((slot) => !slot?.hidden),
    [slotState.slots]
  );

  const slots = React.useMemo(
    () => visibleSlotEntries.map((slot) => slot.time),
    [visibleSlotEntries]
  );

  const unavailable = React.useMemo(
    () =>
      new Set(
        visibleSlotEntries
          .filter((slot) => !slot.available)
          .map((slot) => slot.time)
      ),
    [visibleSlotEntries]
  );

  React.useEffect(() => {
    if (!selectedTime) return;
    if (!slots.includes(selectedTime) || unavailable.has(selectedTime)) {
      setSelectedTime("");
    }
  }, [selectedTime, slots, unavailable]);

  React.useEffect(() => {
    if (slotState.loading || slotState.error || selectedDate !== todayISO()) return;
    if (slots.length > 0) return;
    const nextDate = (bookableDateState.dates || []).find((dateISO) => dateISO > selectedDate);
    if (nextDate) {
      setSelectedDate(nextDate);
    }
  }, [
    bookableDateState.dates,
    selectedDate,
    slotState.error,
    slotState.loading,
    slots.length,
  ]);

  const canConfirm =
    !!selectedDate &&
    !!selectedTime &&
    (!needsFacility || hasFacility) &&
    (actorRole === "doctor" || !!doctor) &&
    slots.includes(selectedTime) &&
    !unavailable.has(selectedTime);

  async function handleConfirm() {
    if (!canConfirm) return;
    if (saving) return;
    setSaveError("");
    setSaving(true);

    const resolvedModeState = await resolveDoctorBookingEntryState({
      doctorId,
      doctor,
      requestedMode: mode,
      consultationType: restored?.consultation_type || null,
      facilityId,
      facilityName,
      facility: restoredFacility,
    });
    const nextMode = resolvedModeState.mode || mode;
    const isEvisit = nextMode === "evisit";
    const nextFacility = isEvisit ? null : restoredFacility;
    const nextFacilityId = isEvisit ? null : facilityId || restored?.practice_id || nextFacility?.id || null;
    const nextFacilityName = isEvisit ? "" : facilityName || nextFacility?.name || "";
    const nextConsultationType = isEvisit
      ? "private"
      : String(restored?.consultation_type || "").toLowerCase() === "private"
        ? "moh"
        : restored?.consultation_type || "moh";

    const next = {
      ...(restored || {}),
      doctor,
      actorRole,
      mode: nextMode,
      previousMode: currentMode,
      previousConsultationType: currentConsultationType,
      facilityId: nextFacilityId,
      facilityName: nextFacilityName,
      facility: nextFacility,
      practice_id: nextFacilityId,
      consultation_type: nextConsultationType,

      selectedDate,
      selectedTime,
      when: {
        ...(restored?.when || {}),
        dateISO: selectedDate,
        time: selectedTime,
      },
      booked_service_price: amountPaid,
      booked_service_currency: currency,
      currency,
      serviceNamePrice:
        Number.isFinite(amountPaid) ? `${amountPaid.toFixed(2)} ${currency}` : restored?.serviceNamePrice || "",
      paymentMethodLabel,
      paymentLabel: paymentMethodLabel,
      paymentMethod: restored?.paymentMethod || null,
      amountPaid,
    };

    persistAppointmentSnapshot(id, next);
    persistAppointmentSnapshotEverywhere(id, next);

    nav(`/appointments/confirm/${id}`, {
      state: { ...next, __flow: "reschedule" },
    });
    setSaving(false);
  }

  const inpersonSelected = mode === "inperson";
  const evisitSelected = mode === "evisit";

  const inpersonDisabled = !modeAccessState.loading && !modeAccessState.hasInPersonOption;
  const evisitDisabled = !modeAccessState.loading && !modeAccessState.evisitEnabled;

  const pillBase =
    "w-full h-11 rounded-[999px] border text-label-large inline-flex items-center justify-center gap-2";

  const pillIdle =
    "bg-[rgb(var(--clr-surface-cont-highest))] text-on-surface-var border-[rgb(var(--clr-outline-variant))]";

  const pillActive =
    "bg-[rgba(29,79,215,0.16)] text-on-surface border-[rgba(29,79,215,0.40)] shadow-[0_3px_6px_-3px_rgba(29,79,215,0.35)]";

  // pure disabled surface-cont-highest (as you asked)
  const pillDisabled =
    "bg-[rgb(var(--clr-surface-cont-highest))] text-on-surface-var border-[rgb(var(--clr-outline-variant))] opacity-70 cursor-not-allowed";

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={t("appointments.edit.title", "Edit appointments")}
          close
          onBack={() => nav(-1)}
        />

        {!doctor && actorRole !== "doctor" ? (
          <Card className="mt-4 px-4 py-4 text-body-medium text-on-surface-var">
            {t(
              "appointments.edit.notFound",
              "Appointment not found. Please go back and open it again."
            )}
            <div className="mt-3">
              <Button fullWidth onClick={() => nav("/appointments")}>
                {t("appointments.edit.backToAppointments", "Back to appointments")}
              </Button>
            </div>
          </Card>
        ) : null}

        {/* Mode selector */}
        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="text-body-medium text-on-surface-var">
              {t("appointments.edit.sections.type", "Type")}
            </div>
            <ContextFeedbackLauncher
              section="appointments-edit-type"
              context={{ surface: "appointments.edit", appointmentId: id, actorRole }}
              entity={{ type: "appointment", id: id || null }}
            />
          </div>

        <div className="chips-row">
          <AfriPill
            label={t("appointments.edit.mode.inPerson", "In-person")}
            value="inperson"
            iconLeft="/icons/appointments/person.svg"
            variant="appointments"
            size="medium"
            isActive={mode === "inperson"}
            onChange={() => {
              if (inpersonDisabled) return;
              setMode("inperson");
            }}
          />

          <AfriPill
            label={t("appointments.edit.mode.eVisit", "Virtual")}
            value="evisit"
            iconLeft="/icons/appointments/video_call.svg"
            variant="appointments"
            size="medium"
            isActive={mode === "evisit"}
            onChange={() => {
              if (evisitDisabled) return;
              setMode("evisit");
            }}
          />
        </div>

          {evisitDisabled ? (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {t(
                "appointments.edit.evisitUnavailable",
                "Virtual consultations are not currently enabled for this doctor, so booking will stay aligned to in-person availability."
              )}
            </div>
          ) : null}

          {mode === "inperson" ? (
            <Card className="mt-3 px-4 py-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-body-medium text-on-surface-var">
                  {t("appointments.edit.sections.location", "Location")}
                </div>
                <div className="text-body-large text-on-surface truncate">
                  {facilityName || t("appointments.edit.chooseFacility", "Choose a facility")}
                </div>
              </div>
              <button
                type="button"
                className="text-label-large text-primary"
                onClick={() => nav(`/appointments/edit-location/${id}`, { state: restored || null })}
              >
                {t("common.change", "Change")}
              </button>
            </Card>
          ) : (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {t(
                "appointments.edit.virtualVisitNotice",
                "This appointment will be a virtual visit."
              )}
            </div>
          )}
        </div>

        {/* Date */}
        <div className="mt-8">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="text-body-medium text-on-surface-var">
              {t("appointments.edit.sections.date", "Date")}
            </div>
            <ContextFeedbackLauncher
              section="appointments-edit-date"
              context={{ surface: "appointments.edit", appointmentId: id, selectedDate }}
              entity={{ type: "appointment", id: id || null }}
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <span className="text-body-large text-on-surface">
              {selectedDate
                ? formatDate(selectedDate, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : t("appointments.edit.selectDate", "Select date")}
            </span>
            <img src="/icons/doctor/calendar_clock.svg" alt="" className="h-5 w-5" draggable="false" />
          </div>

          {bookableDateState.loading ? (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {t("appointments.edit.loading.dates", "Loading available dates...")}
            </div>
          ) : null}

          {!bookableDateState.loading && bookableDateState.error ? (
            <div className="mt-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {bookableDateState.error}
            </div>
          ) : null}

          {!bookableDateState.loading &&
          !bookableDateState.error &&
          bookableDateState.dates.length === 0 ? (
            <div className="mt-3 text-body-medium text-on-surface-var">
              {t(
                "appointments.edit.empty.dates",
                "This doctor has not opened any appointment days yet."
              )}
            </div>
          ) : null}

          {!bookableDateState.loading &&
          !bookableDateState.error &&
          bookableDateState.dates.length > 0 ? (
            <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5">
              {bookableDateState.dates.map((dateISO) => {
                const parts = formatBookableDateParts(dateISO, languageCode);
                const isSelected = selectedDate === dateISO;

                return (
                  <Button
                    key={dateISO}
	                    type="button"
	                    variant={isSelected ? "default" : "tonal"}
	                    onClick={() => setSelectedDate(dateISO)}
	                    className={[
	                      "h-auto min-h-[88px] flex-col gap-1 rounded-[12px] px-3 py-3 text-center",
	                      isSelected
	                        ? "bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))] shadow-[0_4px_10px_rgba(38,82,227,0.18)]"
	                        : "bg-[#DCE6FA] text-[rgb(var(--afrimed-primary))] shadow-none hover:bg-[#D4E0F7]",
	                    ].join(" ")}
                  >
                    <span className="text-label-medium uppercase tracking-[0.04em]">
                      {parts.weekday}
                    </span>
                    <span className="text-title-large leading-none">
                      {parts.day}
                    </span>
                    <span className="text-label-medium">{parts.month}</span>
                  </Button>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Time */}
        <div className="mt-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-body-medium text-on-surface-var">
              {t("appointments.edit.sections.time", "Time")}
            </div>
            <ContextFeedbackLauncher
              section="appointments-edit-time"
              context={{ surface: "appointments.edit", appointmentId: id, selectedDate }}
              entity={{ type: "appointment", id: id || null }}
            />
          </div>

          {slotState.loading ? (
            <div className="mb-3 text-body-medium text-on-surface-var">
              {t("appointments.edit.loading.times", "Loading available times...")}
            </div>
          ) : null}

          {!slotState.loading && slotState.error ? (
            <div className="mb-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {slotState.error}
            </div>
          ) : null}

          {!slotState.loading && !slotState.error && slots.length === 0 ? (
            <div className="mb-3 text-body-medium text-on-surface-var">
              {slotState.workingDay?.enabled === false
                ? t(
                    "appointments.edit.empty.unavailableDay",
                    "This doctor is not available on the selected day."
                  )
                : t(
                    "appointments.edit.empty.noTimes",
                    "No available appointment times remain for this day."
                  )}
            </div>
          ) : null}

          <div className="grid grid-cols-4 gap-3 md:grid-cols-6">
            {slots.map((t) => {
              const isUnavailable = unavailable.has(t);
              const isSelected = selectedTime === t;

              return (
                <Button
                  key={t}
                  type="button"
                  variant={isSelected ? "default" : "tonal"}
                  disabled={isUnavailable}
                  onClick={() => {
                    if (isUnavailable) return;
                    setSelectedTime(t);
                  }}
                  className={[
                    "h-11 px-4 rounded-[12px] text-label-large",
                    isSelected ? "text-[rgb(var(--afrimed-on-primary))]" : "",
                    !isSelected && !isUnavailable ? "bg-[var(--state-primary-12)] text-primary" : "",
                    isUnavailable ? "opacity-50" : "",
                  ].join(" ")}
                >
                  <span className={isUnavailable ? "line-through" : ""}>{t}</span>
                </Button>
              );
            })}
          </div>

          {mode === "inperson" && !hasFacility ? (
            <div className="mt-3 text-body-medium text-[rgb(var(--schemes-critical))]">
              {t(
                "appointments.edit.chooseFacilityError",
                "Please choose a facility for an in-person appointment."
              )}
            </div>
          ) : null}
        </div>

        {/* Payment (read-only) */}
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-body-large text-on-surface-var">
              {t("appointments.edit.sections.payment", "Payment method")}
            </div>
            <ContextFeedbackLauncher
              section="appointments-edit-payment"
              context={{ surface: "appointments.edit", appointmentId: id, paymentMethodLabel }}
              entity={{ type: "appointment", id: id || null }}
            />
          </div>

          <Card>
            <div className="px-4 py-4 flex items-center gap-3">
              <img src="/icons/appointments/credit_card.svg" alt="" className="w-6 h-6 opacity-80" draggable="false" />
              <div className="text-body-large text-on-surface flex-1">{paymentMethodLabel}</div>

              {typeof amountPaid === "number" ? (
                <div className="text-body-large text-on-surface-var">
                  {amountPaid.toFixed(2)} {currency}
                </div>
              ) : null}
            </div>
          </Card>
        </div>

        {/* Actions */}
        <div className="mt-8 flex gap-3">
          <Button variant="ghost" className="flex-1 h-12" onClick={() => nav(-1)}>
            {t("common.cancel", "Cancel")}
          </Button>

          <div className="flex-1">
            {saveError ? (
              <div className="mb-2 rounded-[10px] bg-[rgba(220,38,38,0.08)] px-3 py-2 text-body-medium text-[rgb(var(--schemes-critical))]">
                {saveError}
              </div>
            ) : null}
            <Button className="h-12 w-full" disabled={!canConfirm || saving} onClick={handleConfirm}>
              {saving ? t("common.saving", "Saving...") : t("appointments.edit.confirm", "Confirm")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
